import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    seChangeTheme,
    ThemeService,
    EstensaBusWrapperService,
    seLogoClick,
    seShowViewerInfo,
    seShowClientInfo,
    seUserLogout,
    WadoService,
    seShowEcgPdfOnModal,
    seShowVisitPdfOnModal,
    sePrintEcgPdfOnModal,
    PdfModalActions,
    seShowPdfOnModal,
    seSetModalPdfConfig,
    EstensaWaitingService,
    LoadPdfAction,
    SignalrNotificationsService,
    seShowVisitPdfCertificateOnModal,
    selectHeaderToolbarIsNavigateOnClickEnabled,
    PageUnloadService,
    SsoLoginService,
} from '@se/platform';
import { ModalDialogNotificationService, NotificationService, RouterStackService } from '@se/se.ui.html5';
import { EMPTY, of, throwError, catchError, map, switchMap, tap, withLatestFrom, from, filter } from 'rxjs';

@Injectable()
export class HostEffects {
    setThemeDark$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seChangeTheme),
                tap(({ theme }) => {
                    if (theme === 'dark' && this.themeService.currentTheme.value !== 'estensa-dark') {
                        this.estensaBusService.notify({ messageType: 'NG2_UPDATE_THEME_DARK_MESSAGE' });
                    } else if (theme === 'light' && this.themeService.currentTheme.value !== 'estensa-light') {
                        this.estensaBusService.notify({ messageType: 'NG2_UPDATE_THEME_LIGHT_MESSAGE' });
                    }
                }),
            ),
        { dispatch: false },
    );

    logoClick$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seLogoClick),
                withLatestFrom(this.store.pipe(select(selectHeaderToolbarIsNavigateOnClickEnabled))),
                tap(([_, navigateOnClick]) => {
                    if (!navigateOnClick) {
                        return EMPTY;
                    }

                    return from(
                        this.routerStackService.home().then(() => {
                            this.estensaBusService.notify({ messageType: 'NG2_LOGO_CLICK_MESSAGE' });
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    viewerClick$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seShowViewerInfo),
                tap(() => {
                    this.estensaBusService.notify({ messageType: 'NG2_VIEWER_CLICK_MESSAGE' });

                    return EMPTY;
                }),
            ),
        { dispatch: false },
    );

    clientClick$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seShowClientInfo),
                tap(() => {
                    this.estensaBusService.notify({ messageType: 'NG2_ABOUT_CLICK_MESSAGE' });

                    return EMPTY;
                }),
            ),
        { dispatch: false },
    );

    userLogout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seUserLogout),
                tap(({ forced }) => {
                    if (forced) {
                        this.pageUnload.setIsForced(true);
                    }
                    this.estensaBusService.notify({ messageType: 'NG2_USER_LOGOUT_MESSAGE' });

                    return EMPTY;
                }),
            ),
        { dispatch: false },
    );

    showPdfOnModal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                seShowPdfOnModal,
                seShowEcgPdfOnModal,
                seShowVisitPdfOnModal,
                sePrintEcgPdfOnModal,
                seShowVisitPdfCertificateOnModal,
            ),
            switchMap((action) => {
                const print = action.type === PdfModalActions.PRINT_ECG_PDF_ON_MODAL;
                this.waitingService.isWaiting = true;

                return this.loadPdfFromWado(action).pipe(
                    map((pdf) => ({ pdf, print, visible: true })),
                    catchError((err) => {
                        this.onPdfError(err);
                        return of({ pdf: null, print: false, visible: false });
                    }),
                );
            }),
            map(({ pdf, print, visible }) => {
                this.waitingService.isWaiting = false;

                return seSetModalPdfConfig({ pdf, print, visible });
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private estensaBusService: EstensaBusWrapperService,
        private modalNotification: ModalDialogNotificationService,
        private notification: NotificationService,
        private pageUnload: PageUnloadService,
        private routerStackService: RouterStackService,
        private ssoLoginService: SsoLoginService,
        private store: Store,
        private themeService: ThemeService,
        private translate: TranslateService,
        private wadoService: WadoService,
        private waitingService: EstensaWaitingService,
        signalrNotificationsService: SignalrNotificationsService,
    ) {
        signalrNotificationsService.init();

        estensaBusService.busDataFired
            .pipe(filter((msg) => msg.messageType === 'SSO_LOGIN'))
            .subscribe(() => this.onSsoLogin());
    }

    private onSsoLogin() {
        this.ssoLoginService
            .login()
            .then(() => {
                this.estensaBusService.notify({ messageType: 'NG2_SSO_LOGIN_SUCCESS' });
            })
            .catch((err) => {
                this.estensaBusService.notify({ messageType: 'NG2_SSO_LOGIN_ERROR', data: err });
            });
    }

    private loadPdfFromWado(action: LoadPdfAction) {
        switch (action.type) {
            case PdfModalActions.PRINT_ECG_PDF_ON_MODAL:
                // TODO: 34.13: TROVARE FIX DEFINITIVO
                return this.wadoService
                    .getEcgPdf(action.reportIds, action.displayedSettings, true)
                    .pipe(catchError(() => this.wadoService.getVisitPdf(action.reportIds, action.displayedSettings)));
            case PdfModalActions.SHOW_ECG_PDF_ON_MODAL:
                return this.wadoService.getEcgPdf(action.reportIds, action.displayedSettings);
            case PdfModalActions.SHOW_PDF_ON_MODAL:
                return this.wadoService.getWadoPdf(action.documentId);
            case PdfModalActions.SHOW_VISIT_PDF_ON_MODAL:
                return this.wadoService.getVisitPdf(action.reportIds, action.displayedSettings);
            case PdfModalActions.SHOW_VISIT_PDF_CERTIFICATE_ON_MODAL:
                return this.wadoService.getVisitPdf(action.reportIds, null, action.smCertificateCode);
            default:
                return throwError(() => new Error(`Unknown PDF load type`));
        }
    }

    private onPdfError(err: Error) {
        const msg = this.translate.instant('CannotLoadReport');

        if (err instanceof HttpErrorResponse) {
            this.notification.httpError(err, msg);
        } else {
            this.notification.error(err.message, msg);
        }

        this.modalNotification.error(msg);
    }
}
